/**
 * Die Farben des Diagramms (HM-054).
 *
 * **Serienfarben kommen aus der Größenpalette, Chromfarben aus dem Thema.**
 * Das ist keine Feinheit, sondern die erste der drei Farbachsen (ADR 0013): Die
 * Marke erscheint in keiner Datendarstellung. Bis HM-054 zeichnete der
 * Datenpunkt-Verlauf in `--hm-green-600` — dem Logo-Grün — und damit in
 * derselben Farbe wie der Primärknopf daneben.
 *
 * **Gitter, Text und Grund werden zur Laufzeit aus den CSS-Variablen gelesen**,
 * nicht aus `tokens.ts` eingebrannt. Ein Canvas kann `var(--border-subtle)`
 * nicht auflösen, also muss es jemand tun — und wenn es der Aufbau des
 * Diagramms tut, trägt ein späteres dunkles Thema (OQ-014) automatisch. Steht
 * keine Variable bereit (Test, Server, React Native), gelten die Token als
 * Rückfall; sie sind schließlich die Quelle derselben Werte.
 */
import { colorForQuantity, colors, quantityColorVar } from "../tokens.js";
import type { ChartSeries } from "./types.js";

/**
 * Woher eine Reihe ihre Farbe bekommt (HM-107).
 *
 * Drei Quellen für dieselbe Zuordnung: die **Token** (ohne Browser, React
 * Native), die **CSS-Variablen** als `var(…)` (Legende und Ablesefenster — das
 * DOM löst sie selbst auf) und die **aufgelösten Werte** aus `getComputedStyle`
 * (das Canvas, das keine Variable kennt). Alle drei führen auf dieselbe
 * Quelle, `theme.css` ist aus `tokens.ts` erzeugt — nur trägt das dunkle Thema
 * bei den letzten beiden von selbst mit.
 */
export interface SeriesColorSource {
  readonly quantity: (quantityKey: string) => string;
  readonly datapoint: string;
  /** Dieselbe Farbe, um `amount` (0…1) zum Grund der Karte hin gemischt (HM-118). */
  readonly fade: (color: string, amount: number) => string;
}

/** Die Token, wie sie in `tokens.ts` stehen — das helle Thema, ohne Browser. */
export const TOKEN_SERIES_COLORS: SeriesColorSource = {
  quantity: colorForQuantity,
  datapoint: colors.datapoint,
  fade: (color, amount) => mixColors(color, colors.surface, amount),
};

/** Dieselben Farben als `var(…)` — für alles, was im DOM steht. */
export const CSS_SERIES_COLORS: SeriesColorSource = {
  quantity: quantityColorVar,
  datapoint: "var(--hm-series-datapoint)",
  /* Das DOM mischt selbst — und im Thema, das gerade gilt. */
  fade: (color, amount) =>
    amount <= 0
      ? color
      : `color-mix(in srgb, ${color}, var(--surface-card) ${Math.round(clamp01(amount) * 100)}%)`,
};

export interface ChartPalette extends SeriesColorSource {
  /** Die waagerechten Hilfslinien — zurückhaltend, sie sind kein Inhalt. */
  readonly grid: string;
  /** Achsenbeschriftung. */
  readonly text: string;
  /** Der Grund, auf dem gezeichnet wird — Punkte werden damit umrandet. */
  readonly surface: string;
  /** Die Beschriftung einer Marke: kräftiger als eine Achse, denn sie steht allein. */
  readonly muted: string;
  /** Eine Marke, die auf etwas hinweist. */
  readonly warning: string;
  /** Die Leiste unter einem Abschnitt mit Ersatzwert (HM-115) — keine Größenfarbe, eine Auskunft. */
  readonly substitute: string;
  /** Die Schriftfamilie der Beschriftungen — ein Canvas erbt keine CSS-Schrift. */
  readonly font: string;
}

/** Welche CSS-Variable welchen Rückfall hat. */
const PALETTE_VARS: Readonly<
  Record<Exclude<keyof ChartPalette, keyof SeriesColorSource>, readonly [string, string]>
> = {
  grid: ["--border-subtle", colors.border],
  text: ["--text-faint", colors.textFaint],
  surface: ["--surface-card", colors.surface],
  muted: ["--text-muted", colors.textMuted],
  warning: ["--hm-warning", colors.warning],
  substitute: ["--hm-quality-estimated", colors.textFaint],
  font: ["--font-mono", "ui-monospace, monospace"],
};

/**
 * Liest die Farben des Diagramms aus dem Thema.
 *
 * Ohne Element — oder ohne `getComputedStyle`, das es außerhalb eines Browsers
 * nicht gibt — kommen die Token zurück. Das ist kein Notbehelf: Beide Wege
 * führen auf dieselbe Quelle, `theme.css` ist aus `tokens.ts` erzeugt.
 */
export function readChartPalette(element: Element | null): ChartPalette {
  const style =
    element !== null && typeof globalThis.getComputedStyle === "function"
      ? globalThis.getComputedStyle(element)
      : null;

  const resolve = (variable: string, fallback: string): string => {
    const value = style?.getPropertyValue(variable).trim();
    return value === undefined || value === "" ? fallback : value;
  };
  const read = (name: keyof typeof PALETTE_VARS): string => resolve(...PALETTE_VARS[name]);

  return {
    grid: read("grid"),
    text: read("text"),
    surface: read("surface"),
    muted: read("muted"),
    warning: read("warning"),
    substitute: read("substitute"),
    font: read("font"),
    /**
     * Die Serienfarben ebenso aus dem Thema (HM-107): Ein Canvas kennt kein
     * `var(--hm-quantity-grid-import)`, und aus den Token eingebrannt zeichnete
     * es im dunklen Thema Schiefer auf Schiefer. Unbekannte Größen fallen wie
     * überall auf den Verbrauch zurück — über die Variable, nicht daran vorbei.
     */
    quantity: (quantityKey) => {
      const variable = /^var\((--[^)]+)\)$/.exec(quantityColorVar(quantityKey))?.[1];
      return variable === undefined
        ? colorForQuantity(quantityKey)
        : resolve(variable, colorForQuantity(quantityKey));
    },
    datapoint: resolve("--hm-series-datapoint", colors.datapoint),
    fade: (color, amount) => mixColors(color, resolve("--surface-card", colors.surface), amount),
  };
}

/**
 * Die Farbe je Reihe, in der Reihenfolge der Eingabe.
 *
 * Drei Stufen, und die Reihenfolge ist der ganze Punkt:
 *
 * 1. Eine ausdrücklich gesetzte Farbe gilt. Ausnahme, kein Regelweg.
 * 2. Eine **Energiegröße** bekommt ihre Farbe aus der Größenpalette. Nutzer
 *    lernen sie einmal und erkennen danach jedes Diagramm ohne Legende.
 * 3. Eine Reihe **ohne gedeutete Größe** — der Rohverlauf eines Datenpunkts —
 *    bekommt `colors.datapoint`.
 *
 * **Eine zweite ungedeutete Reihe wird abgelehnt, nicht eingefärbt** (Regel 9).
 * Es gibt genau eine Farbe für „keine Größe"; eine zweite Reihe damit wäre von
 * der ersten nicht zu unterscheiden, und ihr eine Größenfarbe zu leihen hieße,
 * eine Spannung als Netzbezug auszugeben. Wer zwei Datenpunkte vergleichen
 * will, gibt heute Farben an — welche Palette das später übernimmt, ist OQ-032.
 */
export function seriesColors(
  series: readonly ChartSeries[],
  source: SeriesColorSource = TOKEN_SERIES_COLORS,
): readonly string[] {
  let plain = 0;

  return series.map((entry) => {
    if (entry.color !== undefined) return entry.color;
    if (entry.quantity !== undefined) {
      const base = source.quantity(entry.quantity);
      return entry.fade === undefined || entry.fade <= 0 ? base : source.fade(base, entry.fade);
    }

    plain += 1;
    if (plain > 1) {
      throw new Error(
        `Diagramm: mehr als eine Reihe ohne Energiegröße („${entry.label}"). ` +
          "Für Reihen ohne gedeutete Größe gibt es eine Farbe; weitere brauchen " +
          "eine ausdrückliche `color` oder eine `quantity`.",
      );
    }
    return source.datapoint;
  });
}

/**
 * Dieselbe Farbe durchscheinend.
 *
 * **Auch `#fff` und `rgb(…)`, nicht nur `#rrggbb`** (HM-106). Die Farben aus
 * dem Thema kommen zur Laufzeit aus den CSS-Variablen — und aus dem
 * **gebauten** Stylesheet, in dem der Minifier `#ffffff` zu `#fff` gekürzt
 * hat. Die erste Fassung ließ alles liegen, was kein sechsstelliges Hex war,
 * mit der Begründung, dann habe jemand die Farbe von Hand gesetzt. Für den
 * Schleier über einer Teilsumme hieß das: im Entwicklungsserver 55 % Weiß,
 * im Betrieb **deckendes** Weiß — jeder Balken mit Abdeckung war ein weißer
 * Kasten. Lokal nicht zu sehen, weil Vite das Stylesheet dort nicht kürzt.
 *
 * Was sich nicht deuten lässt, kommt weiter unverändert zurück; wer einen
 * Schleier daraus macht, prüft das mit `hasAlpha`.
 */
export function withAlpha(color: string, alpha: number): string {
  const clamped = Math.min(1, Math.max(0, alpha));
  const trimmed = color.trim();

  const short = /^#([0-9a-f])([0-9a-f])([0-9a-f])$/i.exec(trimmed);
  const long = /^#([0-9a-f]{6})$/i.exec(trimmed);
  const rgb = /^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*(?:,[^)]*)?\)$/i.exec(trimmed);

  if (long !== null || short !== null) {
    const hex =
      long !== null
        ? long[1]!
        : `${short![1]}${short![1]}${short![2]}${short![2]}${short![3]}${short![3]}`;
    const value = Math.round(clamped * 255);
    return `#${hex}${value.toString(16).padStart(2, "0")}`;
  }
  if (rgb !== null) return `rgba(${rgb[1]}, ${rgb[2]}, ${rgb[3]}, ${clamped})`;
  return trimmed;
}

/** Ob `withAlpha` diese Farbe durchscheinend machen konnte. */
export function hasAlpha(color: string): boolean {
  return withAlpha(color, 0.5) !== color.trim();
}

function clamp01(value: number): number {
  return Math.min(1, Math.max(0, value));
}

/** `#rgb`, `#rrggbb` oder `rgb(…)` als Kanäle — `null`, was sich nicht deuten lässt. */
function channels(color: string): readonly [number, number, number] | null {
  const trimmed = color.trim();
  const short = /^#([0-9a-f])([0-9a-f])([0-9a-f])$/i.exec(trimmed);
  if (short !== null) {
    return [short[1]!, short[2]!, short[3]!].map((c) => Number.parseInt(c + c, 16)) as unknown as [
      number,
      number,
      number,
    ];
  }
  const long = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(trimmed);
  if (long !== null) {
    return [long[1]!, long[2]!, long[3]!].map((c) => Number.parseInt(c, 16)) as unknown as [
      number,
      number,
      number,
    ];
  }
  const rgb = /^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*(?:,[^)]*)?\)$/i.exec(trimmed);
  if (rgb !== null) return [Number(rgb[1]), Number(rgb[2]), Number(rgb[3])];
  return null;
}

/**
 * Zwei Farben gemischt, `amount` = Anteil der zweiten (HM-118). Das Canvas
 * kennt kein `color-mix`, also rechnet es jemand. Was sich nicht deuten lässt,
 * kommt unverändert zurück — lieber die volle Farbe als eine geratene.
 */
export function mixColors(color: string, toward: string, amount: number): string {
  const from = channels(color);
  const to = channels(toward);
  if (from === null || to === null) return color;
  const t = clamp01(amount);
  const hex = from
    .map((c, i) => Math.round(c + (to[i]! - c) * t).toString(16).padStart(2, "0"))
    .join("");
  return `#${hex}`;
}
